import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { 
  Sparkles, MessageSquare, Copy, Check, Loader2, 
  RefreshCw, Send, Smile, Briefcase, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const toneConfig = {
  friendly: { label: 'Amigável', icon: Smile, color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' },
  formal: { label: 'Formal', icon: Briefcase, color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' },
  urgent: { label: 'Urgente', icon: AlertTriangle, color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' },
};

export const AIReminderButton = ({ 
  memberName, 
  amount, 
  dueDate, 
  tripName, 
  daysOverdue = 0,
  onSend 
}) => {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState('');
  const [editedMessage, setEditedMessage] = useState('');
  const [copied, setCopied] = useState(false);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const response = await api.post('/ai/payment-reminder-suggestions', {
        member_name: memberName,
        amount,
        due_date: dueDate,
        trip_name: tripName,
        days_overdue: daysOverdue
      });
      setSuggestions(response.data.suggestions);
      if (response.data.suggestions.length > 0) {
        const firstSuggestion = response.data.suggestions[0].message;
        setSelectedMessage(firstSuggestion);
        setEditedMessage(firstSuggestion);
      }
    } catch (error) {
      toast.error('Erro ao gerar sugestões');
      // Fallback
      const fallback = `Olá ${memberName}! 👋\n\nLembrete amigável sobre o pagamento de R$ ${amount.toFixed(2)} para "${tripName}".\n\nData limite: ${dueDate}\n\nQualquer dúvida, é só falar!`;
      setSuggestions([{ tone: 'friendly', message: fallback }]);
      setSelectedMessage(fallback);
      setEditedMessage(fallback);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    fetchSuggestions();
  };

  const handleSelectSuggestion = (message) => {
    setSelectedMessage(message);
    setEditedMessage(message);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(editedMessage);
      setCopied(true);
      toast.success('Mensagem copiada!');
      await new Promise(resolve => setTimeout(resolve, 2000));
      setCopied(false);
    } catch {
      toast.error('Erro ao copiar');
    }
  };

  const handleSend = () => {
    if (onSend) {
      onSend(editedMessage);
    }
    setOpen(false);
    toast.success('Mensagem enviada!');
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleOpen}
        className="text-orange-600 hover:text-orange-700"
        data-testid="ai-reminder-btn"
      >
        <Sparkles className="w-4 h-4 mr-1" />
        Lembrete IA
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-orange-500" />
              Gerar Lembrete de Pagamento
            </DialogTitle>
            <DialogDescription>
              Escolha um tom ou edite a mensagem antes de enviar para {memberName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Info */}
            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm">
              <p><span className="font-medium">Valor:</span> R$ {amount.toFixed(2)}</p>
              <p><span className="font-medium">Vencimento:</span> {dueDate}</p>
              {daysOverdue > 0 && (
                <p className="text-red-600"><span className="font-medium">Atraso:</span> {daysOverdue} dias</p>
              )}
            </div>

            {/* Tone Selection */}
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                <span className="ml-2 text-slate-500 dark:text-slate-400">Gerando sugestões...</span>
              </div>
            ) : (
              <>
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Escolha o tom:</p>
                  <div className="flex flex-wrap gap-2">
                    {suggestions.map((suggestion) => {
                      const config = toneConfig[suggestion.tone] || toneConfig.friendly;
                      const ToneIcon = config.icon;
                      const isSelected = selectedMessage === suggestion.message;
                      return (
                        <Badge
                          key={suggestion.tone}
                          className={`cursor-pointer px-3 py-1.5 ${
                            isSelected 
                              ? 'bg-orange-500 text-white hover:bg-orange-600' 
                              : config.color + ' hover:opacity-80'
                          }`}
                          onClick={() => handleSelectSuggestion(suggestion.message)}
                        >
                          <ToneIcon className="w-3 h-3 mr-1" />
                          {config.label}
                        </Badge>
                      );
                    })}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={fetchSuggestions}
                      className="h-7"
                    >
                      <RefreshCw className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                {/* Message Preview/Edit */}
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Mensagem:</p>
                  <Textarea
                    value={editedMessage}
                    onChange={(e) => setEditedMessage(e.target.value)}
                    rows={5}
                    className="resize-none"
                    placeholder="A mensagem aparecerá aqui..."
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCopy}
              disabled={!editedMessage}
            >
              {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              {copied ? 'Copiado!' : 'Copiar'}
            </Button>
            <Button
              onClick={handleSend}
              disabled={!editedMessage}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Send className="w-4 h-4 mr-1" />
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const GroupReminderButton = ({ tripId, onSend }) => {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const fetchGroupReminder = async () => {
    setLoading(true);
    try {
      const response = await api.post('/ai/group-reminder', { trip_id: tripId });
      setMessage(response.data.message);
    } catch (error) {
      toast.error('Erro ao gerar mensagem');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    fetchGroupReminder();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success('Mensagem copiada!');
    } catch {
      toast.error('Erro ao copiar');
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={handleOpen}
        className="rounded-full"
        data-testid="group-reminder-btn"
      >
        <MessageSquare className="w-4 h-4 mr-2" />
        Lembrete Geral
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-orange-500" />
              Lembrete para o Grupo
            </DialogTitle>
            <DialogDescription>
              Mensagem gerada por IA para lembrar todos os pagamentos pendentes
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
              </div>
            ) : (
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                className="resize-none"
              />
            )}
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setMessage('');
                fetchGroupReminder();
              }}
              disabled={loading}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Regenerar
            </Button>
            <Button
              variant="outline"
              onClick={handleCopy}
              disabled={!message}
            >
              <Copy className="w-4 h-4 mr-1" />
              Copiar
            </Button>
            <Button
              onClick={() => {
                if (onSend) onSend(message);
                setOpen(false);
                toast.success('Mensagem enviada ao grupo!');
              }}
              disabled={!message}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Send className="w-4 h-4 mr-1" />
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AIReminderButton;
